import React from 'react'
import { LucideIcon } from 'lucide-react'

interface EmptyStateProps {
  icon?: string | LucideIcon
  title: string
  description: string
  actionLabel?: string
  actionHref?: string
  onAction?: () => void
  variant?: 'default' | 'compact'
  className?: string
}

/**
 * Reusable empty state component for dashboard pages.
 * Provides consistent styling and optional call-to-action.
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  actionLabel,
  actionHref,
  onAction,
  variant = 'default',
  className = ''
}) => {
  const isCompact = variant === 'compact'
  const padding = isCompact ? 'py-8' : 'py-12'

  const renderIcon = () => {
    if (!icon) return null

    if (typeof icon === 'string') {
      // Emoji icon
      return <div className={`${isCompact ? 'text-3xl' : 'text-4xl'} mb-2`}>{icon}</div>
    }

    // Lucide icon component
    const IconComponent = icon
    return (
      <div className={`${isCompact ? 'w-10 h-10' : 'w-12 h-12'} mx-auto mb-3 text-gray-400`}>
        <IconComponent className="w-full h-full" />
      </div>
    )
  }

  const renderAction = () => {
    if (!actionLabel) return null

    const buttonClasses = `
      mt-4 inline-flex items-center gap-2 px-4 py-2
      bg-blue-600 hover:bg-blue-700 text-white
      rounded-lg font-medium text-sm transition-colors
    `

    if (actionHref) {
      return (
        <a href={actionHref} className={buttonClasses}>
          {actionLabel}
        </a>
      )
    }

    if (onAction) {
      return (
        <button onClick={onAction} className={buttonClasses}>
          {actionLabel}
        </button>
      )
    }

    return null
  }

  return (
    <div className={`card text-center ${padding} ${className}`}>
      {renderIcon()}
      <h3 className={`${isCompact ? 'text-base' : 'text-lg'} font-semibold text-gray-900 mb-2`}>
        {title}
      </h3>
      <p className={`text-gray-600 ${isCompact ? 'text-sm' : ''} max-w-md mx-auto`}>
        {description}
      </p>
      {renderAction()}
    </div>
  )
}

export default EmptyState
